/** All dates are stored in UTC and displayed in this zone. */
export const APP_TIMEZONE = 'Europe/Warsaw';
export const APP_LOCALE = 'pl-PL';

export const ROLES = ['admin', 'worker'] as const;
export type Role = (typeof ROLES)[number];
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Kierownik',
  worker: 'Pracownik',
};

export const VISIT_STATUSES = ['planned', 'done', 'cancelled'] as const;
export type VisitStatus = (typeof VISIT_STATUSES)[number];
export const VISIT_STATUS_LABELS: Record<VisitStatus, string> = {
  planned: 'Zaplanowana',
  done: 'Wykonana',
  cancelled: 'Odwołana',
};

export const PAYMENT_STATUSES = ['unpaid', 'paid'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];
export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  unpaid: 'Nieopłacone',
  paid: 'Opłacone',
};

export const PAYMENT_METHODS = ['cash', 'transfer', 'card', 'blik'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Gotówka',
  transfer: 'Przelew',
  card: 'Karta',
  blik: 'BLIK',
};

export const VISIBILITIES = ['private', 'selected'] as const;
export type Visibility = (typeof VISIBILITIES)[number];

/**
 * Colour keys a manager can assign to a user. The actual colour values live in
 * the web app's design tokens (`--user-<key>` / `--user-<key>-ink`).
 */
export const USER_COLORS = [
  'blue',
  'green',
  'purple',
  'orange',
  'teal',
  'red',
  'pink',
  'brown',
  'slate',
  'olive',
] as const;
export type UserColor = (typeof USER_COLORS)[number];
export const USER_COLOR_LABELS: Record<UserColor, string> = {
  blue: 'Niebieski',
  green: 'Zielony',
  purple: 'Fioletowy',
  orange: 'Pomarańczowy',
  teal: 'Morski',
  red: 'Czerwony',
  pink: 'Różowy',
  brown: 'Brązowy',
  slate: 'Grafitowy',
  olive: 'Oliwkowy',
};

export const NAV_APPS = ['google', 'waze', 'apple'] as const;
export type NavApp = (typeof NAV_APPS)[number];
export const NAV_APP_LABELS: Record<NavApp, string> = {
  google: 'Google Maps',
  waze: 'Waze',
  apple: 'Mapy Apple',
};

/** Quick-pick visit durations in minutes. */
export const DURATION_PRESETS = [30, 60, 90, 120] as const;
export const DEFAULT_DURATION_MINUTES = 60;

/** Reminder lead times in minutes offered in settings; `null` means "off". */
export const REMINDER_OPTIONS = [15, 30, 60, 120] as const;
export const DEFAULT_REMINDER_MINUTES = 60;
export const DEFAULT_MORNING_SUMMARY_TIME = '07:00';

/** Notifications more than this late (e.g. after the server was down) are dropped, not sent. */
export const NOTIFICATION_MAX_LATE_MINUTES = 15;

/**
 * Push services the server may send to (host suffixes). The endpoint URL comes from
 * the browser, so anything else is refused to keep the server from calling arbitrary hosts.
 */
export const PUSH_SERVICE_HOST_SUFFIXES = [
  '.googleapis.com', // Chrome, Edge on Android, Samsung Internet (FCM)
  '.mozilla.com', // Firefox
  '.push.apple.com', // Safari on iOS/iPadOS/macOS
  '.notify.windows.com', // Edge on Windows (WNS)
] as const;

/** Offline copy of the calendar: from this many days back to this many days ahead. */
export const OFFLINE_DAYS_BACK = 7;
export const OFFLINE_DAYS_AHEAD = 14;
